
import { useState, useEffect, useRef } from 'react';

interface CallScenario {
  id: string;
  industry: string;
  icon: string;
  color: string;
  bgGradient: string;
  callerName: string;
  businessName: string;
  transcript: { role: 'ai' | 'caller'; text: string }[];
  outcome: string;
  outcomeIcon: string;
}

const scenarios: CallScenario[] = [
  {
    id: 'plumber',
    industry: 'Plumber',
    icon: 'ri-drop-fill',
    color: 'text-sky-500',
    bgGradient: 'from-sky-500 to-cyan-500',
    callerName: 'Mike R.',
    businessName: 'QuickFlow Plumbing',
    transcript: [
      { role: 'ai', text: 'Hi, thanks for calling QuickFlow Plumbing! How can I help you today?' },
      { role: 'caller', text: 'I have a burst pipe in my basement — water is everywhere!' },
      { role: 'ai', text: "I'm sorry to hear that! Let me get an emergency technician to you ASAP. What's your address?" },
      { role: 'caller', text: '742 Maple Drive, unit 3.' },
      { role: 'ai', text: "Got it. I've booked an emergency visit for today between 2–3 PM. You'll get a confirmation text shortly." },
    ],
    outcome: 'Emergency visit booked',
    outcomeIcon: 'ri-calendar-check-fill',
  },
  {
    id: 'salon',
    industry: 'Hair Salon',
    icon: 'ri-scissors-fill',
    color: 'text-pink-500',
    bgGradient: 'from-pink-500 to-rose-500',
    callerName: 'Jessica T.',
    businessName: 'Luxe Hair Studio',
    transcript: [
      { role: 'ai', text: 'Hello, welcome to Luxe Hair Studio! How can I assist you?' },
      { role: 'caller', text: "Hi! I'd like to book a balayage appointment for this Saturday." },
      { role: 'ai', text: 'Great choice! We have openings at 10 AM and 1 PM with stylist Mia. Which works better?' },
      { role: 'caller', text: '1 PM sounds perfect.' },
      { role: 'ai', text: "Done! You're booked with Mia at 1 PM Saturday. We'll send a reminder the day before." },
    ],
    outcome: 'Appointment confirmed',
    outcomeIcon: 'ri-calendar-check-fill',
  },
  {
    id: 'medspa',
    industry: 'Med Spa',
    icon: 'ri-heart-pulse-fill',
    color: 'text-emerald-500',
    bgGradient: 'from-emerald-500 to-teal-500',
    callerName: 'Sarah L.',
    businessName: 'Glow Aesthetics',
    transcript: [
      { role: 'ai', text: 'Thank you for calling Glow Aesthetics! How may I help you today?' },
      { role: 'caller', text: "I'm interested in Botox but I've never had it. Can you tell me about pricing?" },
      { role: 'ai', text: 'Of course! Our Botox starts at $12 per unit. Most clients need 20–40 units. We also offer a free consultation.' },
      { role: 'caller', text: 'That sounds great. Can I book a consultation?' },
      { role: 'ai', text: "Absolutely! I've scheduled you for a free consultation this Thursday at 3 PM with Dr. Patel." },
    ],
    outcome: 'Free consultation booked',
    outcomeIcon: 'ri-stethoscope-fill',
  },
  {
    id: 'contractor',
    industry: 'Contractor',
    icon: 'ri-hammer-fill',
    color: 'text-amber-500',
    bgGradient: 'from-amber-500 to-orange-500',
    callerName: 'David K.',
    businessName: 'Summit Builders',
    transcript: [
      { role: 'ai', text: "Hi, you've reached Summit Builders! How can I help?" },
      { role: 'caller', text: 'I need a quote for a kitchen remodel — full gut renovation.' },
      { role: 'ai', text: "We'd love to help! I can schedule a free on-site estimate. What days work for you this week?" },
      { role: 'caller', text: 'Wednesday afternoon would be ideal.' },
      { role: 'ai', text: "Perfect. You're set for Wednesday at 2 PM. Our project manager Tom will be there with a detailed estimate." },
    ],
    outcome: 'On-site estimate scheduled',
    outcomeIcon: 'ri-file-list-3-fill',
  },
  {
    id: 'electrician',
    industry: 'Electrician',
    icon: 'ri-flashlight-fill',
    color: 'text-yellow-500',
    bgGradient: 'from-yellow-500 to-amber-500',
    callerName: 'Karen W.',
    businessName: 'BrightSpark Electric',
    transcript: [
      { role: 'ai', text: 'Hello, BrightSpark Electric! What can I do for you today?' },
      { role: 'caller', text: 'Half the outlets in my living room stopped working after a storm.' },
      { role: 'ai', text: 'That could be a tripped breaker or storm damage. Let me get a licensed electrician out to diagnose it.' },
      { role: 'caller', text: 'How soon can someone come?' },
      { role: 'ai', text: "We have a slot tomorrow morning at 9 AM. I've booked it for you — you'll receive a text confirmation now." },
    ],
    outcome: 'Service call booked',
    outcomeIcon: 'ri-calendar-check-fill',
  },
];

function WaveformBar({ delay, isPlaying }: { delay: number; isPlaying: boolean }) {
  return (
    <div
      className="w-1 rounded-full transition-colors duration-300"
      style={{
        animation: isPlaying ? `waveform 0.8s ease-in-out ${delay}s infinite alternate` : 'none',
        height: isPlaying ? undefined : '4px',
        backgroundColor: isPlaying ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)',
      }}
    />
  );
}

function CallSimulation({
  scenario,
  isActive,
}: {
  scenario: CallScenario;
  isActive: boolean;
}) {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [showOutcome, setShowOutcome] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!isActive) {
      setVisibleMessages(0);
      setShowOutcome(false);
      autoTimerRef.current.forEach(clearTimeout);
      autoTimerRef.current = [];
      return;
    }

    setVisibleMessages(0);
    setShowOutcome(false);
    autoTimerRef.current.forEach(clearTimeout);
    autoTimerRef.current = [];

    const totalMessages = scenario.transcript.length;
    let delay = 500;
    for (let i = 0; i < totalMessages; i++) {
      const t = setTimeout(() => {
        setVisibleMessages(i + 1);
        if (i === totalMessages - 1) {
          const outcomeTimer = setTimeout(() => setShowOutcome(true), 600);
          autoTimerRef.current.push(outcomeTimer);
        }
      }, delay);
      autoTimerRef.current.push(t);
      delay += 2000;
    }

    return () => {
      autoTimerRef.current.forEach(clearTimeout);
      autoTimerRef.current = [];
    };
  }, [isActive, scenario]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [visibleMessages]);

  return (
    <div
      className={`transition-all duration-500 ${
        isActive
          ? 'opacity-100 scale-100'
          : 'opacity-0 scale-95 pointer-events-none absolute inset-0'
      }`}
    >
      <div className="bg-gray-950 rounded-3xl overflow-hidden shadow-2xl border border-gray-800 max-w-md mx-auto">
        {/* Call Header */}
        <div className={`bg-gradient-to-r ${scenario.bgGradient} p-5 relative overflow-hidden`}>
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 0.5px, transparent 0)',
              backgroundSize: '20px 20px',
            }}
          />
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className={`${scenario.icon} text-white text-lg`}></i>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{scenario.businessName}</p>
                <p className="text-white/70 text-xs">AI Receptionist Active</p>
              </div>
            </div>
            <div className="flex items-center gap-1 h-5">
              {[0, 0.12, 0.24, 0.36, 0.48].map((d, i) => (
                <WaveformBar key={i} delay={d} isPlaying={isActive} />
              ))}
            </div>
          </div>
          <div className="relative z-10 mt-3 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
            <span className="text-white/80 text-xs">
              Incoming call from {scenario.callerName}
            </span>
          </div>
        </div>

        {/* Chat Transcript */}
        <div
          ref={chatRef}
          className="p-4 space-y-3 h-72 overflow-y-auto bg-gray-950 scroll-smooth"
        >
          {scenario.transcript.slice(0, visibleMessages).map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${
                msg.role === 'caller' ? 'justify-end' : 'justify-start'
              } animate-fadeSlideUp`}
            >
              <div
                className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'ai'
                    ? 'bg-gray-800 text-gray-200 rounded-bl-md'
                    : 'bg-gradient-to-r ' + scenario.bgGradient + ' text-white rounded-br-md'
                }`}
              >
                <p className="text-[10px] font-semibold mb-1 opacity-60">
                  {msg.role === 'ai' ? 'AI Receptionist' : scenario.callerName}
                </p>
                {msg.text}
              </div>
            </div>
          ))}

          {visibleMessages < scenario.transcript.length && visibleMessages > 0 && (
            <div className="flex justify-start animate-fadeSlideUp">
              <div className="bg-gray-800 px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5">
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Outcome Banner */}
        <div className={`transition-all duration-500 overflow-hidden ${showOutcome ? 'max-h-20' : 'max-h-0'}`}>
          <div className={`bg-gradient-to-r ${scenario.bgGradient} mx-4 mb-4 rounded-xl p-3 flex items-center gap-3`}>
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center shrink-0">
              <i className={`${scenario.outcomeIcon} text-white`}></i>
            </div>
            <div>
              <p className="text-white text-xs font-semibold">{scenario.outcome}</p>
              <p className="text-white/70 text-[10px]">Handled automatically by AI</p>
            </div>
            <i className="ri-check-double-fill text-white/80 text-lg ml-auto"></i>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AnimatedCalls() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % scenarios.length);
      }, 12000);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isAutoPlaying]);

  const handleSelect = (idx: number) => {
    setActiveIndex(idx);
    setIsAutoPlaying(false);
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    // Resume autoplay after a short pause so the user can read the selected scenario
    setTimeout(() => setIsAutoPlaying(true), 20000);
  };

  return (
    <section className="py-24 px-6 bg-gray-50">
      <style>{`
        @keyframes waveform {
          0% { height: 4px; }
          100% { height: 18px; }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeSlideUp {
          animation: fadeSlideUp 0.35s ease-out forwards;
        }
      `}</style>

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-5">
            <i className="ri-chat-voice-fill text-teal-600 text-base"></i>
            <span className="text-sm font-medium text-teal-700">Live AI Call Demos</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            One AI. Every Industry.
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            See how our AI receptionist handles real‑world calls across different service businesses —
            booking appointments, answering questions, and converting leads 24/7.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Industry Selector */}
          <div className="space-y-3 order-2 lg:order-1">
            {scenarios.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => handleSelect(idx)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer group ${
                  activeIndex === idx
                    ? 'border-teal-500 bg-white shadow-lg shadow-teal-500/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                      activeIndex === idx
                        ? `bg-gradient-to-r ${s.bgGradient} text-white shadow-md`
                        : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${s.icon} text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className={`font-semibold text-sm ${activeIndex === idx ? 'text-gray-900' : 'text-gray-700'}`}>
                        {s.industry}
                      </h3>
                      {activeIndex === idx && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
                          <i className="ri-eye-fill text-[10px]"></i>
                          Watching
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {s.businessName} — {s.callerName} calling
                    </p>
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      &ldquo;{s.transcript[1].text}&rdquo;
                    </p>
                  </div>
                  <i className={`ri-arrow-right-s-line text-lg transition-transform ${activeIndex === idx ? 'text-teal-500 translate-x-0.5' : 'text-gray-300'}`}></i>
                </div>
              </button>
            ))}

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                <p className="text-2xl font-bold text-gray-900">5</p>
                <p className="text-xs text-gray-500 mt-1">Industries</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                <p className="text-2xl font-bold text-gray-900">{"<30s"}</p>
                <p className="text-xs text-gray-500 mt-1">Avg. Handle Time</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                <p className="text-2xl font-bold text-gray-900">100%</p>
                <p className="text-xs text-gray-500 mt-1">Calls Answered</p>
              </div>
            </div>
          </div>

          {/* Animated Call Display */}
          <div className="relative order-1 lg:order-2 lg:sticky lg:top-28">
            {scenarios.map((s, idx) => (
              <CallSimulation key={s.id} scenario={s} isActive={activeIndex === idx} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
